/**
 * Disability Form Page
 * Multi-step form for creating and editing disabilities (PCD)
 *
 * Steps:
 *  0 - Dados do Funcionário
 *  1 - Classificação e Diagnóstico
 *  2 - Médico e Período
 *  3 - Descrições e Conclusão
 *  4 - Laudo Caracterizador
 */

import React, { useEffect, useState, useCallback } from 'react';
import {
  Paper,
  Typography,
  Box,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Switch,
  CircularProgress,
  Alert,
  Autocomplete,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import {
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  NavigateNext,
  NavigateBefore,
  Person as PersonIcon,
  Category as CategoryIcon,
  LocalHospital as LocalHospitalIcon,
  Description as DescriptionIcon,
  Assignment as AssignmentIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { PageShell } from '../../components';
import { CompanySelectionModal, CompanyInfoBox } from '../../components/forms';
import { CidAutocomplete } from '../medical-leaves/components/CidAutocomplete';
import { useCompanySelection } from '../../hooks/useCompanySelection';
import { disabilityService } from '../../services/disabilityService';
import { getApiErrorMessage } from '../../types/common';
import { employeeService } from '../../services/employeeService';
import { Employee, EmployeeStatus } from '../../types/employee';
import { BRAZILIAN_STATES } from '../../types/common';
import {
  DisabilityCreateFormData,
  DisabilityUpdateFormData,
  DISABILITY_TYPE_OPTIONS,
  SEVERITY_LEVEL_OPTIONS,
  DISABILITY_CLASSIFICATION_OPTIONS,
  CLASSIFICATION_ORIGIN_OPTIONS,
  CLASSIFICATION_TYPE_OPTIONS,
  DISABILITY_CONCLUSION_OPTIONS,
  LaudoDetail,
} from '../../types/disability';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const steps = [
  'Dados do Funcionário',
  'Classificação e Diagnóstico',
  'Médico e Período',
  'Descrições e Conclusão',
  'Laudo Caracterizador',
];

const stepIcons = [
  <PersonIcon key="0" sx={{ color: 'primary.main', fontSize: 22 }} />,
  <CategoryIcon key="1" sx={{ color: 'warning.main', fontSize: 22 }} />,
  <LocalHospitalIcon key="2" sx={{ color: 'error.main', fontSize: 22 }} />,
  <DescriptionIcon key="3" sx={{ color: 'success.main', fontSize: 22 }} />,
  <AssignmentIcon key="4" sx={{ color: 'info.main', fontSize: 22 }} />,
];

// ---------------------------------------------------------------------------
// Form state
// ---------------------------------------------------------------------------

interface FormData {
  // step 0
  employeeId: number;
  employeeName: string;
  companyId: string;
  departmentName: string;
  occupationName: string;
  // step 1
  classification: string;
  classificationOrigin: string;
  classificationType: string;
  disabilityType: string;
  diagnosis: string;
  cidCode: string;
  cidDescription: string;
  // step 2
  doctorName: string;
  doctorUf: string;
  doctorCrm: string;
  startDate: string;
  endDate: string;
  isRehabilitated: string; // 'Sim' | 'Não' | ''
  severity: string;
  // step 3
  changesDescription: string;
  limitationsDescription: string;
  disabilityDescription: string;
  conclusion: string;
  accommodations: string;
  // step 4
  laudoDetail: LaudoDetail;
  // meta
  registrationDate: string;
  isActive: boolean;
}

const emptyLaudoDetail: LaudoDetail = {
  tipoFisica: false,
  tipoAuditiva: false,
  tipoVisual: false,
  tipoMonocular: false,
  tipoIntelectual: false,
  tipoPsicossocial: false,
  tipoAutista: false,
  tipoMultipla: false,
  visualCegueira: false,
  visualBaixaVisao: false,
  visualCampoVisual: false,
  intelComunicacao: false,
  intelCuidadoPessoal: false,
  intelHabilidadesSociais: false,
  intelRecursosComunidade: false,
  intelSaudeSeguranca: false,
  intelHabilidadesAcademicas: false,
  intelLazer: false,
  intelTrabalho: false,
  fisicaNanismoAltura: '',
  fisicaOutras: '',
  intelIdadeInicio: '',
};

const emptyForm: FormData = {
  employeeId: 0,
  employeeName: '',
  companyId: '',
  departmentName: '',
  occupationName: '',
  classification: '',
  classificationOrigin: '',
  classificationType: '',
  disabilityType: '',
  diagnosis: '',
  cidCode: '',
  cidDescription: '',
  doctorName: '',
  doctorUf: '',
  doctorCrm: '',
  startDate: '',
  endDate: '',
  isRehabilitated: '',
  severity: '',
  changesDescription: '',
  limitationsDescription: '',
  disabilityDescription: '',
  conclusion: '',
  accommodations: '',
  laudoDetail: { ...emptyLaudoDetail },
  registrationDate: new Date().toISOString().split('T')[0],
  isActive: true,
};

// ---------------------------------------------------------------------------
// CRM mask helper (from ProfessionalForm.tsx)
// ---------------------------------------------------------------------------

function formatCRM(value: string): string {
  const digits = value.replace(/[^0-9A-Za-z]/g, '');
  if (digits.length <= 6) return digits.replace(/\D/g, '');
  const num = digits.slice(0, 6).replace(/\D/g, '');
  const uf = digits.slice(6, 8).toUpperCase().replace(/[^A-Z]/g, '');
  return `${num}/${uf}`;
}

// ---------------------------------------------------------------------------
// Section wrapper (card-based style)
//
// NÃO é o `SectionCard` de `components/common/DetailView` — aquele é o bloco da
// tela de VISUALIZAÇÃO (borderTop com accentColor, badge/action no cabeçalho).
// Este é um agrupador de campos de FORMULÁRIO (cabeçalho em `action.hover`, sem
// accent), e layout de formulário ainda não é padronizado — ver a skill
// `frontend-screens`.
// ---------------------------------------------------------------------------

const FormSection: React.FC<{
  icon: React.ReactNode;
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}> = ({ icon, title, subtitle, children }) => (
  <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2.5, py: 1.5, bgcolor: 'action.hover', borderBottom: '1px solid', borderColor: 'divider' }}>
      {icon}
      <Typography variant="subtitle1" fontWeight={600}>{title}</Typography>
      {subtitle && <Typography variant="body2" color="text.secondary">{subtitle}</Typography>}
    </Box>
    <Box sx={{ p: 2.5 }}>{children}</Box>
  </Box>
);

// ===========================================================================
// Component
// ===========================================================================

const DisabilityFormPage: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { id } = useParams<{ id: string }>();
  const isEditMode = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeStep, setActiveStep] = useState(0);

  const [formData, setFormData] = useState<FormData>({ ...emptyForm });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const {
    modalOpen,
    selectedCompany,
    showInfoBox,
    openModal,
    handleSelect: handleCompanySelect,
  } = useCompanySelection({ isEditMode, currentCompanyId: String(formData.companyId ?? '') });

  // Employee autocomplete state
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [employeesLoading, setEmployeesLoading] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);
  const [employeeInputValue, setEmployeeInputValue] = useState('');

  // -----------------------------------------------------------------------
  // Auto-fill company when field is hidden
  // -----------------------------------------------------------------------
  useEffect(() => {
    if (selectedCompany && selectedCompany.id && formData.companyId !== selectedCompany.id) {
      setFormData((prev) => ({ ...prev, companyId: selectedCompany.id }));
    }
  }, [selectedCompany, formData.companyId]);

  // -----------------------------------------------------------------------
  // Fetch employees when company changes
  // -----------------------------------------------------------------------
  const fetchEmployees = useCallback(async (companyId: string) => {
    if (!companyId) {
      setEmployees([]);
      return;
    }

    setEmployeesLoading(true);
    try {
      const response = await employeeService.getEmployees({
        limit: 100,
        sortBy: 'name',
        sortOrder: 'ASC',
        filters: {
          companyId: parseInt(companyId, 10),
          status: EmployeeStatus.ATIVO,
        },
      });
      setEmployees(response.data);
    } catch (err) {
      console.error('Error fetching employees:', err);
      setEmployees([]);
    } finally {
      setEmployeesLoading(false);
    }
  }, []);

  useEffect(() => {
    if (formData.companyId) {
      fetchEmployees(formData.companyId);
    }
  }, [formData.companyId, fetchEmployees]);

  // -----------------------------------------------------------------------
  // Load disability in edit mode
  // -----------------------------------------------------------------------
  const loadDisability = useCallback(async (disabilityId: number) => {
    try {
      setLoading(true);
      setError(null);
      const d = await disabilityService.getDisabilityById(disabilityId);

      setFormData({
        employeeId: d.employeeId || 0,
        employeeName: d.employeeName,
        companyId: d.companyId?.toString() || '',
        departmentName: d.departmentName || '',
        occupationName: d.occupationName || '',
        classification: d.classification || '',
        classificationOrigin: d.classificationOrigin || '',
        classificationType: d.classificationType || '',
        disabilityType: d.disabilityType || '',
        diagnosis: d.diagnosis || '',
        cidCode: d.cidCode || '',
        cidDescription: d.cidDescription || '',
        doctorName: d.doctorName || '',
        doctorUf: d.doctorUf || '',
        doctorCrm: d.doctorCrm || '',
        startDate: d.startDate || '',
        endDate: d.endDate || '',
        isRehabilitated: d.isRehabilitated === true ? 'Sim' : d.isRehabilitated === false ? 'Não' : '',
        severity: d.severity || '',
        changesDescription: d.changesDescription || '',
        limitationsDescription: d.limitationsDescription || '',
        disabilityDescription: d.disabilityDescription || '',
        conclusion: d.conclusion || '',
        accommodations: d.accommodations || '',
        laudoDetail: d.laudoDetail ?? { ...emptyLaudoDetail },
        registrationDate: d.registrationDate || new Date().toISOString().split('T')[0],
        isActive: d.isActive,
      });
    } catch (err) {
      console.error('Error loading disability:', err);
      const errorMessage = getApiErrorMessage(err, 'Erro ao carregar deficiência');
      setError(errorMessage);
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    if (isEditMode && id) {
      loadDisability(parseInt(id));
    }
  }, [id, isEditMode, loadDisability]);

  // Set selected employee when employees are loaded and we have employeeId
  // Also auto-fill department/occupation from Employee data (edit mode)
  useEffect(() => {
    if (formData.employeeId && employees.length > 0) {
      const employee = employees.find((e) => e.id === formData.employeeId);
      if (employee) {
        setSelectedEmployee(employee);
        setEmployeeInputValue(employee.name);
        setFormData((prev) => ({
          ...prev,
          departmentName: employee.department?.name || prev.departmentName,
          occupationName: employee.occupation?.name || prev.occupationName,
        }));
      }
    }
  }, [formData.employeeId, employees]);

  // -----------------------------------------------------------------------
  // Field handlers
  // -----------------------------------------------------------------------
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = event.target;
    const type = (event.target as HTMLInputElement).type;
    const checked = (event.target as HTMLInputElement).checked;

    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));

    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleSelectChange = (name: string, value: string) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => {
        const n = { ...prev };
        delete n[name];
        return n;
      });
    }
  };

  const handleEmployeeChange = (_event: React.SyntheticEvent, newValue: Employee | null) => {
    setSelectedEmployee(newValue);
    if (newValue) {
      setFormData((prev) => ({
        ...prev,
        employeeId: newValue.id,
        employeeName: newValue.name,
        departmentName: newValue.department?.name || '',
        occupationName: newValue.occupation?.name || '',
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        employeeId: 0,
        employeeName: '',
        departmentName: '',
        occupationName: '',
      }));
    }
    if (errors.employeeId) {
      setErrors((prev) => { const n = { ...prev }; delete n.employeeId; return n; });
    }
  };

  const handleCidChange = (code: string, description: string) => {
    setFormData((prev) => ({
      ...prev,
      cidCode: code,
      cidDescription: description,
    }));
  };

  const handleCrmChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({ ...prev, doctorCrm: formatCRM(e.target.value) }));
  };

  const handleLaudoBool = (field: keyof LaudoDetail, checked: boolean) => {
    setFormData((prev) => ({
      ...prev,
      laudoDetail: { ...prev.laudoDetail, [field]: checked },
    }));
  };

  const handleLaudoText = (field: keyof LaudoDetail, value: string) => {
    setFormData((prev) => ({
      ...prev,
      laudoDetail: { ...prev.laudoDetail, [field]: value },
    }));
  };

  // -----------------------------------------------------------------------
  // Per-step validation
  // -----------------------------------------------------------------------
  const validateStep = (step: number): boolean => {
    const newErrors: Record<string, string> = {};

    if (step === 0) {
      if (!formData.companyId) newErrors.companyId = 'Empresa é obrigatória';
      if (!formData.employeeId) newErrors.employeeId = 'Funcionário é obrigatório';
    }

    if (step === 1) {
      if (!formData.classification) newErrors.classification = 'Classificação é obrigatória';
    }

    if (step === 2) {
      if (!formData.startDate) newErrors.startDate = 'Data de início é obrigatória';
      if (formData.doctorCrm && !/^\d{6}\/[A-Z]{2}$/.test(formData.doctorCrm)) {
        newErrors.doctorCrm = 'CRM deve estar no formato 000000/UF';
      }
      if (formData.startDate && formData.endDate && formData.endDate < formData.startDate) {
        newErrors.endDate = 'Data de fim deve ser posterior à data de início';
      }
    }

    // Step 3: no required fields

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // -----------------------------------------------------------------------
  // Step navigation
  // -----------------------------------------------------------------------
  const handleNext = (e?: React.MouseEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (!validateStep(activeStep)) {
      enqueueSnackbar('Por favor, corrija os erros antes de avançar', { variant: 'warning' });
      return;
    }
    setActiveStep((prev) => Math.min(prev + 1, steps.length - 1));
  };

  const handleBack = () => {
    setActiveStep((prev) => Math.max(prev - 1, 0));
  };

  // -----------------------------------------------------------------------
  // Submit
  // -----------------------------------------------------------------------
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    // Guard: only submit on the last step
    if (activeStep < steps.length - 1) {
      return;
    }

    // Validate all steps
    for (let s = 0; s < steps.length; s++) {
      if (!validateStep(s)) {
        setActiveStep(s);
        enqueueSnackbar('Por favor, corrija os erros no formulário', { variant: 'warning' });
        return;
      }
    }

    try {
      setSaving(true);

      const isRehabilitatedBool = formData.isRehabilitated === 'Sim' ? true : formData.isRehabilitated === 'Não' ? false : undefined;

      if (isEditMode && id) {
        const updateData: DisabilityUpdateFormData = {
          employeeId: formData.employeeId,
          employeeName: formData.employeeName,
          disabilityType: formData.disabilityType || undefined,
          disabilityDescription: formData.disabilityDescription || undefined,
          severity: formData.severity || undefined,
          accommodations: formData.accommodations || undefined,
          registrationDate: formData.registrationDate || undefined,
          isActive: formData.isActive,
          departmentName: formData.departmentName || undefined,
          occupationName: formData.occupationName || undefined,
          classification: formData.classification || undefined,
          classificationOrigin: formData.classificationOrigin || undefined,
          classificationType: formData.classificationType || undefined,
          diagnosis: formData.diagnosis || undefined,
          doctorName: formData.doctorName || undefined,
          doctorUf: formData.doctorUf || undefined,
          doctorCrm: formData.doctorCrm || undefined,
          startDate: formData.startDate || undefined,
          endDate: formData.endDate || undefined,
          isRehabilitated: isRehabilitatedBool,
          cidCode: formData.cidCode || undefined,
          cidDescription: formData.cidDescription || undefined,
          changesDescription: formData.changesDescription || undefined,
          limitationsDescription: formData.limitationsDescription || undefined,
          conclusion: formData.conclusion || undefined,
          laudoDetail: formData.laudoDetail,
        };
        await disabilityService.updateDisability(parseInt(id), updateData);
        enqueueSnackbar('Deficiência atualizada com sucesso!', { variant: 'success' });
      } else {
        const createData: DisabilityCreateFormData = {
          employeeId: formData.employeeId,
          employeeName: formData.employeeName,
          companyId: parseInt(formData.companyId, 10),
          disabilityType: formData.disabilityType || 'Física',
          disabilityDescription: formData.disabilityDescription || 'Conforme laudo médico',
          severity: formData.severity || 'Moderada',
          accommodations: formData.accommodations || undefined,
          registrationDate: formData.registrationDate,
          departmentName: formData.departmentName || undefined,
          occupationName: formData.occupationName || undefined,
          classification: formData.classification || undefined,
          classificationOrigin: formData.classificationOrigin || undefined,
          classificationType: formData.classificationType || undefined,
          diagnosis: formData.diagnosis || undefined,
          doctorName: formData.doctorName || undefined,
          doctorUf: formData.doctorUf || undefined,
          doctorCrm: formData.doctorCrm || undefined,
          startDate: formData.startDate || undefined,
          endDate: formData.endDate || undefined,
          isRehabilitated: isRehabilitatedBool,
          cidCode: formData.cidCode || undefined,
          cidDescription: formData.cidDescription || undefined,
          changesDescription: formData.changesDescription || undefined,
          limitationsDescription: formData.limitationsDescription || undefined,
          conclusion: formData.conclusion || undefined,
          laudoDetail: formData.laudoDetail,
        };
        await disabilityService.createDisability(createData);
        enqueueSnackbar('Deficiência registrada com sucesso!', { variant: 'success' });
      }

      navigate('/disabilities');
    } catch (err) {
      console.error('Error saving disability:', err);
      enqueueSnackbar(
        getApiErrorMessage(err, `Erro ao ${isEditMode ? 'atualizar' : 'registrar'} deficiência`),
        { variant: 'error' }
      );
    } finally {
      setSaving(false);
    }
  };

  const handleGoBack = () => {
    navigate('/disabilities');
  };

  const breadcrumbs = [
    { label: 'Início', path: '/' },
    { label: 'Deficiências PCD', path: '/disabilities' },
    { label: isEditMode ? 'Editar' : 'Registrar' },
  ];

  // -----------------------------------------------------------------------
  // Loading state
  // -----------------------------------------------------------------------
  if (loading) {
    return (
      <PageShell
        breadcrumbs={breadcrumbs}
        title={isEditMode ? 'Editar Deficiência' : 'Registrar Deficiência'}
      >
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
          <CircularProgress />
        </Box>
      </PageShell>
    );
  }

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------
  return (
    <PageShell
      breadcrumbs={breadcrumbs}
      title={isEditMode ? 'Editar Deficiência' : 'Registrar Deficiência'}
      actions={
        <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={handleGoBack}>
          Voltar
        </Button>
      }
    >
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Paper sx={{ p: 3 }}>
          {showInfoBox && selectedCompany && (
            <CompanyInfoBox
              companyName={selectedCompany.name}
              onEdit={openModal}
            />
          )}

          {/* Stepper */}
          <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          <form onSubmit={handleSubmit}>
            {/* ============================================================ */}
            {/* STEP 0 — Dados do Funcionário                                */}
            {/* ============================================================ */}
            {activeStep === 0 && (
              <FormSection icon={stepIcons[0]} title="Dados do Funcionário">
                <Grid container spacing={2}>
                  {/* Funcionário */}
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Autocomplete
                      value={selectedEmployee}
                      onChange={handleEmployeeChange}
                      inputValue={employeeInputValue}
                      onInputChange={(_event, v) => setEmployeeInputValue(v)}
                      options={employees}
                      getOptionLabel={(o) => o.name}
                      loading={employeesLoading}
                      disabled={saving || !formData.companyId}
                      isOptionEqualToValue={(o, v) => o.id === v.id}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          required
                          label="Funcionário"
                          placeholder={formData.companyId ? 'Selecione o funcionário' : 'Selecione uma empresa primeiro'}
                          error={Boolean(errors.employeeId)}
                          helperText={errors.employeeId || (!formData.companyId ? 'Selecione uma empresa primeiro' : undefined)}
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {employeesLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                      noOptionsText={!formData.companyId ? 'Selecione uma empresa primeiro' : 'Nenhum funcionário encontrado'}
                      loadingText="Carregando..."
                    />
                  </Grid>

                  {/* Setor (read-only) */}
                  <Grid size={{ xs: 12, md: 3 }}>
                    <TextField
                      fullWidth
                      label="Setor"
                      value={formData.departmentName}
                      InputProps={{ readOnly: true }}
                      sx={{ '& .MuiInputBase-root': { bgcolor: 'action.hover' } }}
                    />
                  </Grid>

                  {/* Cargo (read-only) */}
                  <Grid size={{ xs: 12, md: 3 }}>
                    <TextField
                      fullWidth
                      label="Cargo"
                      value={formData.occupationName}
                      InputProps={{ readOnly: true }}
                      sx={{ '& .MuiInputBase-root': { bgcolor: 'action.hover' } }}
                    />
                  </Grid>

                  {/* Data de Registro (hidden but always sent) */}
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth
                      type="date"
                      label="Data de Registro"
                      name="registrationDate"
                      value={formData.registrationDate}
                      onChange={handleInputChange}
                      InputLabelProps={{ shrink: true }}
                      disabled={saving}
                    />
                  </Grid>

                  {/* Active Status (only in edit mode) */}
                  {isEditMode && (
                    <Grid size={{ xs: 12, md: 6 }}>
                      <FormControlLabel
                        control={
                          <Switch
                            name="isActive"
                            checked={formData.isActive}
                            onChange={handleInputChange}
                            disabled={saving}
                          />
                        }
                        label="Registro Ativo"
                      />
                    </Grid>
                  )}
                </Grid>
              </FormSection>
            )}

            {/* ============================================================ */}
            {/* STEP 1 — Classificação e Diagnóstico                         */}
            {/* ============================================================ */}
            {activeStep === 1 && (
              <FormSection icon={stepIcons[1]} title="Classificação e Diagnóstico">
                <Grid container spacing={2}>
                  {/* Classificação * */}
                  <Grid size={{ xs: 12, md: 4 }}>
                    <FormControl fullWidth required error={Boolean(errors.classification)}>
                      <InputLabel>Classificação</InputLabel>
                      <Select
                        value={formData.classification}
                        label="Classificação"
                        onChange={(e) => handleSelectChange('classification', e.target.value)}
                        disabled={saving}
                      >
                        {DISABILITY_CLASSIFICATION_OPTIONS.map((opt) => (
                          <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                        ))}
                      </Select>
                      {errors.classification && (
                        <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                          {errors.classification}
                        </Typography>
                      )}
                    </FormControl>
                  </Grid>

                  {/* Origem de Classificação */}
                  <Grid size={{ xs: 12, md: 4 }}>
                    <FormControl fullWidth>
                      <InputLabel>Origem de Classificação</InputLabel>
                      <Select
                        value={formData.classificationOrigin}
                        label="Origem de Classificação"
                        onChange={(e) => handleSelectChange('classificationOrigin', e.target.value)}
                        disabled={saving}
                      >
                        <MenuItem value="">Nenhuma</MenuItem>
                        {CLASSIFICATION_ORIGIN_OPTIONS.map((opt) => (
                          <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* Tipo de Classificação */}
                  <Grid size={{ xs: 12, md: 4 }}>
                    <FormControl fullWidth>
                      <InputLabel>Tipo de Classificação</InputLabel>
                      <Select
                        value={formData.classificationType}
                        label="Tipo de Classificação"
                        onChange={(e) => handleSelectChange('classificationType', e.target.value)}
                        disabled={saving}
                      >
                        <MenuItem value="">Nenhum</MenuItem>
                        {CLASSIFICATION_TYPE_OPTIONS.map((opt) => (
                          <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* Tipo de Deficiência (existing) */}
                  <Grid size={{ xs: 12, md: 4 }}>
                    <FormControl fullWidth>
                      <InputLabel>Tipo de Deficiência</InputLabel>
                      <Select
                        value={formData.disabilityType}
                        label="Tipo de Deficiência"
                        onChange={(e) => handleSelectChange('disabilityType', e.target.value)}
                        disabled={saving}
                      >
                        <MenuItem value="">Nenhum</MenuItem>
                        {DISABILITY_TYPE_OPTIONS.map((type) => (
                          <MenuItem key={type} value={type}>{type}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* Severidade */}
                  <Grid size={{ xs: 12, md: 4 }}>
                    <FormControl fullWidth>
                      <InputLabel>Severidade</InputLabel>
                      <Select
                        value={formData.severity}
                        label="Severidade"
                        onChange={(e) => handleSelectChange('severity', e.target.value)}
                        disabled={saving}
                      >
                        <MenuItem value="">Nenhuma</MenuItem>
                        {SEVERITY_LEVEL_OPTIONS.map((level) => (
                          <MenuItem key={level} value={level}>{level}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* Diagnóstico */}
                  <Grid size={{ xs: 12, md: 4 }}>
                    <TextField
                      fullWidth
                      label="Diagnóstico"
                      name="diagnosis"
                      value={formData.diagnosis}
                      onChange={handleInputChange}
                      disabled={saving}
                      placeholder="Diagnóstico médico"
                    />
                  </Grid>

                  {/* CID */}
                  <Grid size={{ xs: 12, md: 4 }}>
                    <CidAutocomplete
                      value={formData.cidCode}
                      onChange={handleCidChange}
                      disabled={saving}
                    />
                  </Grid>

                  {/* Descrição do CID (read-only, auto-fill) */}
                  <Grid size={{ xs: 12, md: 8 }}>
                    <TextField
                      fullWidth
                      label="Descrição do CID"
                      value={formData.cidDescription}
                      InputProps={{ readOnly: true }}
                      sx={{ '& .MuiInputBase-root': { bgcolor: 'action.hover' } }}
                    />
                  </Grid>
                </Grid>
              </FormSection>
            )}

            {/* ============================================================ */}
            {/* STEP 2 — Médico e Período                                    */}
            {/* ============================================================ */}
            {activeStep === 2 && (
              <FormSection icon={stepIcons[2]} title="Médico e Período">
                <Grid container spacing={2}>
                  {/* Médico */}
                  <Grid size={{ xs: 12, md: 5 }}>
                    <TextField
                      fullWidth
                      label="Médico"
                      name="doctorName"
                      value={formData.doctorName}
                      onChange={handleInputChange}
                      disabled={saving}
                      placeholder="Nome do médico responsável"
                    />
                  </Grid>

                  {/* UF */}
                  <Grid size={{ xs: 12, md: 2 }}>
                    <FormControl fullWidth>
                      <InputLabel>UF</InputLabel>
                      <Select
                        value={formData.doctorUf}
                        label="UF"
                        onChange={(e) => handleSelectChange('doctorUf', e.target.value)}
                        disabled={saving}
                      >
                        <MenuItem value="">-</MenuItem>
                        {BRAZILIAN_STATES.map((uf) => (
                          <MenuItem key={uf} value={uf}>{uf}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* CRM */}
                  <Grid size={{ xs: 12, md: 3 }}>
                    <TextField
                      fullWidth
                      label="CRM"
                      value={formData.doctorCrm}
                      onChange={handleCrmChange}
                      error={Boolean(errors.doctorCrm)}
                      helperText={errors.doctorCrm}
                      disabled={saving}
                      placeholder="000000/UF"
                      inputProps={{ maxLength: 9 }}
                    />
                  </Grid>

                  {/* spacer for alignment */}
                  <Grid size={{ xs: 12, md: 2 }} />

                  {/* Data Início * */}
                  <Grid size={{ xs: 12, md: 4 }}>
                    <TextField
                      fullWidth
                      required
                      type="date"
                      label="Data Início"
                      name="startDate"
                      value={formData.startDate}
                      onChange={handleInputChange}
                      error={Boolean(errors.startDate)}
                      helperText={errors.startDate}
                      InputLabelProps={{ shrink: true }}
                      disabled={saving}
                    />
                  </Grid>

                  {/* Data Fim */}
                  <Grid size={{ xs: 12, md: 4 }}>
                    <TextField
                      fullWidth
                      type="date"
                      label="Data Fim"
                      name="endDate"
                      value={formData.endDate}
                      onChange={handleInputChange}
                      error={Boolean(errors.endDate)}
                      helperText={errors.endDate}
                      InputLabelProps={{ shrink: true }}
                      disabled={saving}
                    />
                  </Grid>

                  {/* Reabilitado */}
                  <Grid size={{ xs: 12, md: 4 }}>
                    <FormControl fullWidth>
                      <InputLabel>Reabilitado</InputLabel>
                      <Select
                        value={formData.isRehabilitated}
                        label="Reabilitado"
                        onChange={(e) => handleSelectChange('isRehabilitated', e.target.value)}
                        disabled={saving}
                      >
                        <MenuItem value="">-</MenuItem>
                        <MenuItem value="Sim">Sim</MenuItem>
                        <MenuItem value="Não">Não</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </FormSection>
            )}

            {/* ============================================================ */}
            {/* STEP 3 — Descrições e Conclusão                              */}
            {/* ============================================================ */}
            {activeStep === 3 && (
              <FormSection icon={stepIcons[3]} title="Descrições e Conclusão">
                <Grid container spacing={2}>
                  {/* Alterações */}
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      fullWidth
                      multiline
                      rows={4}
                      label="Descrição das alterações físicas, sensoriais, intelectuais e mentais"
                      name="changesDescription"
                      value={formData.changesDescription}
                      onChange={handleInputChange}
                      disabled={saving}
                    />
                  </Grid>

                  {/* Limitações */}
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      fullWidth
                      multiline
                      rows={4}
                      label="Descrição das limitações funcionais e apoios necessários"
                      name="limitationsDescription"
                      value={formData.limitationsDescription}
                      onChange={handleInputChange}
                      disabled={saving}
                    />
                  </Grid>

                  {/* Descrição da Deficiência (existing) */}
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      label="Descrição da Deficiência"
                      name="disabilityDescription"
                      value={formData.disabilityDescription}
                      onChange={handleInputChange}
                      disabled={saving}
                      placeholder="Descreva detalhadamente a deficiência..."
                    />
                  </Grid>

                  {/* Conclusão + Acomodações */}
                  <Grid size={{ xs: 12, md: 6 }}>
                    <FormControl fullWidth>
                      <InputLabel>Conclusão</InputLabel>
                      <Select
                        value={formData.conclusion}
                        label="Conclusão"
                        onChange={(e) => handleSelectChange('conclusion', e.target.value)}
                        disabled={saving}
                      >
                        <MenuItem value="">Nenhuma</MenuItem>
                        {DISABILITY_CONCLUSION_OPTIONS.map((opt) => (
                          <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid size={{ xs: 12 }}>
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      label="Acomodações Necessárias"
                      name="accommodations"
                      value={formData.accommodations}
                      onChange={handleInputChange}
                      disabled={saving}
                      placeholder="Descreva as adaptações e acomodações necessárias no ambiente de trabalho..."
                    />
                  </Grid>
                </Grid>
              </FormSection>
            )}

            {/* ============================================================ */}
            {/* STEP 4 — Laudo Caracterizador                                */}
            {/* ============================================================ */}
            {activeStep === 4 && (
              <FormSection icon={stepIcons[4]} title="Laudo Caracterizador de Deficiência">
                <Grid container spacing={2}>
                  {/* I - Deficiência Física */}
                  <Grid size={{ xs: 12, md: 6 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.laudoDetail.tipoFisica}
                          onChange={(e) => handleLaudoBool('tipoFisica', e.target.checked)}
                          disabled={saving}
                        />
                      }
                      label={
                        <Typography variant="body2">
                          <strong>I- Deficiência Física</strong> — alteração completa ou parcial de um ou mais
                          segmentos do corpo humano, acarretando o comprometimento da função física,
                          apresentando-se sob a forma de paraplegia, paraparesia, monoplegia, monoparesia,
                          tetraplegia, tetraparesia, triplegia, triparesia, hemiplegia, hemiparesia, ostomia,
                          amputação ou ausência de membro, paralisia cerebral, membros com deformidade
                          congênita ou adquirida, nanismo, outras.
                        </Typography>
                      }
                    />
                    <Box sx={{ pl: 4, display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
                      <TextField
                        size="small"
                        label="Nanismo (altura)"
                        value={formData.laudoDetail.fisicaNanismoAltura}
                        onChange={(e) => handleLaudoText('fisicaNanismoAltura', e.target.value)}
                        disabled={saving || !formData.laudoDetail.tipoFisica}
                      />
                      <TextField
                        size="small"
                        label="Outras (especificar)"
                        value={formData.laudoDetail.fisicaOutras}
                        onChange={(e) => handleLaudoText('fisicaOutras', e.target.value)}
                        disabled={saving || !formData.laudoDetail.tipoFisica}
                      />
                    </Box>
                  </Grid>

                  {/* I - Deficiência Auditiva */}
                  <Grid size={{ xs: 12, md: 6 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.laudoDetail.tipoAuditiva}
                          onChange={(e) => handleLaudoBool('tipoAuditiva', e.target.checked)}
                          disabled={saving}
                        />
                      }
                      label={
                        <Typography variant="body2">
                          <strong>I- Deficiência Auditiva</strong> — perda bilateral, parcial ou total, de 41
                          decibéis (dB) ou mais, aferida por audiograma nas frequências de 500HZ, 1.000HZ,
                          2.000Hz e 3.000Hz. <em>Obs: Anexar audiograma.</em>
                        </Typography>
                      }
                    />
                  </Grid>

                  {/* II - Deficiência Visual */}
                  <Grid size={{ xs: 12, md: 6 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.laudoDetail.tipoVisual}
                          onChange={(e) => handleLaudoBool('tipoVisual', e.target.checked)}
                          disabled={saving}
                        />
                      }
                      label={<Typography variant="body2"><strong>II- Deficiência Visual</strong></Typography>}
                    />
                    <Box sx={{ pl: 4, display: 'flex', flexDirection: 'column' }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            size="small"
                            checked={formData.laudoDetail.visualCegueira}
                            onChange={(e) => handleLaudoBool('visualCegueira', e.target.checked)}
                            disabled={saving || !formData.laudoDetail.tipoVisual}
                          />
                        }
                        label={
                          <Typography variant="body2">
                            cegueira: acuidade visual igual ou menor que 0,05 (20/400) no melhor olho, com a
                            melhor correção óptica;
                          </Typography>
                        }
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            size="small"
                            checked={formData.laudoDetail.visualBaixaVisao}
                            onChange={(e) => handleLaudoBool('visualBaixaVisao', e.target.checked)}
                            disabled={saving || !formData.laudoDetail.tipoVisual}
                          />
                        }
                        label={
                          <Typography variant="body2">
                            baixa visão: acuidade visual entre 0,3 (20/60) e 0,05 (20/400) no melhor olho, com
                            a melhor correção óptica;
                          </Typography>
                        }
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            size="small"
                            checked={formData.laudoDetail.visualCampoVisual}
                            onChange={(e) => handleLaudoBool('visualCampoVisual', e.target.checked)}
                            disabled={saving || !formData.laudoDetail.tipoVisual}
                          />
                        }
                        label={
                          <Typography variant="body2">
                            somatória da medida do campo visual em ambos os olhos igual ou menor que 60°.
                          </Typography>
                        }
                      />
                      <Typography variant="caption" color="text.secondary">
                        Obs: Anexar laudo oftalmológico, utilizar tabela Snellen para avaliar acuidade visual.
                      </Typography>
                    </Box>
                  </Grid>

                  {/* III b - Visão Monocular */}
                  <Grid size={{ xs: 12, md: 6 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.laudoDetail.tipoMonocular}
                          onChange={(e) => handleLaudoBool('tipoMonocular', e.target.checked)}
                          disabled={saving}
                        />
                      }
                      label={
                        <Typography variant="body2">
                          <strong>III b- Visão Monocular</strong> — conforme parecer CONJUR/MTE 444/11: cegueira
                          na qual a acuidade visual com a melhor correção óptica é igual ou menor que 0,05
                          (20/400) em um olho (ou aquela declarada por oftalmologista).
                        </Typography>
                      }
                    />
                  </Grid>

                  {/* IV - Deficiência Intelectual */}
                  <Grid size={{ xs: 12 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.laudoDetail.tipoIntelectual}
                          onChange={(e) => handleLaudoBool('tipoIntelectual', e.target.checked)}
                          disabled={saving}
                        />
                      }
                      label={
                        <Typography variant="body2">
                          <strong>IV- Deficiência Intelectual</strong> — funcionamento intelectual
                          significativamente inferior à média, com manifestação antes dos 18 anos e limitações
                          associadas a duas ou mais áreas de habilidades adaptativas, tais como:
                        </Typography>
                      }
                    />
                    <Grid container sx={{ pl: 4 }}>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              size="small"
                              checked={formData.laudoDetail.intelComunicacao}
                              onChange={(e) => handleLaudoBool('intelComunicacao', e.target.checked)}
                              disabled={saving || !formData.laudoDetail.tipoIntelectual}
                            />
                          }
                          label={<Typography variant="body2">a) Comunicação</Typography>}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              size="small"
                              checked={formData.laudoDetail.intelCuidadoPessoal}
                              onChange={(e) => handleLaudoBool('intelCuidadoPessoal', e.target.checked)}
                              disabled={saving || !formData.laudoDetail.tipoIntelectual}
                            />
                          }
                          label={<Typography variant="body2">b) Cuidado pessoal</Typography>}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              size="small"
                              checked={formData.laudoDetail.intelHabilidadesSociais}
                              onChange={(e) => handleLaudoBool('intelHabilidadesSociais', e.target.checked)}
                              disabled={saving || !formData.laudoDetail.tipoIntelectual}
                            />
                          }
                          label={<Typography variant="body2">c) Habilidades sociais</Typography>}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              size="small"
                              checked={formData.laudoDetail.intelRecursosComunidade}
                              onChange={(e) => handleLaudoBool('intelRecursosComunidade', e.target.checked)}
                              disabled={saving || !formData.laudoDetail.tipoIntelectual}
                            />
                          }
                          label={<Typography variant="body2">d) Utilização de recursos da comunidade</Typography>}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              size="small"
                              checked={formData.laudoDetail.intelSaudeSeguranca}
                              onChange={(e) => handleLaudoBool('intelSaudeSeguranca', e.target.checked)}
                              disabled={saving || !formData.laudoDetail.tipoIntelectual}
                            />
                          }
                          label={<Typography variant="body2">e) Saúde e segurança</Typography>}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              size="small"
                              checked={formData.laudoDetail.intelHabilidadesAcademicas}
                              onChange={(e) => handleLaudoBool('intelHabilidadesAcademicas', e.target.checked)}
                              disabled={saving || !formData.laudoDetail.tipoIntelectual}
                            />
                          }
                          label={<Typography variant="body2">f) Habilidades acadêmicas</Typography>}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              size="small"
                              checked={formData.laudoDetail.intelLazer}
                              onChange={(e) => handleLaudoBool('intelLazer', e.target.checked)}
                              disabled={saving || !formData.laudoDetail.tipoIntelectual}
                            />
                          }
                          label={<Typography variant="body2">g) Lazer</Typography>}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              size="small"
                              checked={formData.laudoDetail.intelTrabalho}
                              onChange={(e) => handleLaudoBool('intelTrabalho', e.target.checked)}
                              disabled={saving || !formData.laudoDetail.tipoIntelectual}
                            />
                          }
                          label={<Typography variant="body2">h) Trabalho</Typography>}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                          size="small"
                          label="Idade de início"
                          value={formData.laudoDetail.intelIdadeInicio}
                          onChange={(e) => handleLaudoText('intelIdadeInicio', e.target.value)}
                          disabled={saving || !formData.laudoDetail.tipoIntelectual}
                        />
                      </Grid>
                    </Grid>
                    <Typography variant="caption" color="text.secondary" sx={{ pl: 4, display: 'block' }}>
                      Obs: Anexar laudo do especialista.
                    </Typography>
                  </Grid>

                  {/* IV a - Deficiência Mental - Psicossocial */}
                  <Grid size={{ xs: 12, md: 6 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.laudoDetail.tipoPsicossocial}
                          onChange={(e) => handleLaudoBool('tipoPsicossocial', e.target.checked)}
                          disabled={saving}
                        />
                      }
                      label={
                        <Typography variant="body2">
                          <strong>IV a- Deficiência Mental – Psicossocial</strong> — conforme Convenção ONU:
                          esquizofrenia, outros transtornos psicóticos, outras limitações psicossociais.
                          Informar se há outras doenças associadas e data de início da manifestação da doença
                          (assinalar também as limitações para habilidades adaptativas no quadro acima).{' '}
                          <em>Obs: Anexar laudo do especialista.</em>
                        </Typography>
                      }
                    />
                  </Grid>

                  {/* Ib - Deficiência Mental - Espectro Autista */}
                  <Grid size={{ xs: 12, md: 6 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.laudoDetail.tipoAutista}
                          onChange={(e) => handleLaudoBool('tipoAutista', e.target.checked)}
                          disabled={saving}
                        />
                      }
                      label={
                        <Typography variant="body2">
                          <strong>Ib- Deficiência Mental – Espectro Autista</strong> — Lei 12764/2012.{' '}
                          <em>Obs: Anexar laudo do especialista.</em>
                        </Typography>
                      }
                    />
                  </Grid>

                  {/* V - Deficiência Múltipla */}
                  <Grid size={{ xs: 12, md: 6 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.laudoDetail.tipoMultipla}
                          onChange={(e) => handleLaudoBool('tipoMultipla', e.target.checked)}
                          disabled={saving}
                        />
                      }
                      label={
                        <Typography variant="body2">
                          <strong>V- Deficiência Múltipla</strong> — associação de duas ou mais deficiências.
                          (Assinalar cada uma acima.)
                        </Typography>
                      }
                    />
                  </Grid>
                </Grid>
              </FormSection>
            )}

            {/* ============================================================ */}
            {/* Navigation buttons                                           */}
            {/* ============================================================ */}
            <Box display="flex" justifyContent="space-between" mt={3}>
              <Button
                type="button"
                variant="outlined"
                startIcon={activeStep === 0 ? <ArrowBackIcon /> : <NavigateBefore />}
                onClick={activeStep === 0 ? handleGoBack : handleBack}
                disabled={saving}
              >
                {activeStep === 0 ? 'Cancelar' : 'Voltar'}
              </Button>

              {activeStep < steps.length - 1 ? (
                <Button
                  type="button"
                  variant="contained"
                  endIcon={<NavigateNext />}
                  onClick={handleNext}
                  disabled={saving}
                >
                  Próximo
                </Button>
              ) : (
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                  disabled={saving}
                >
                  {saving ? 'Salvando...' : isEditMode ? 'Atualizar' : 'Registrar'}
                </Button>
              )}
            </Box>
          </form>
        </Paper>

      <CompanySelectionModal
        open={modalOpen}
        onSelect={handleCompanySelect}
      />
    </PageShell>
  );
};

export default DisabilityFormPage;
